import { Stack, Text } from '@chakra-ui/react';
import { NestedHome } from '@habity/types';
import { NearbyWithEditor } from './NearbyWithEditor';
import { NearbyGoogle } from './NearbyGoogle';
import { ProfileMap } from './Map';
import { getNearbyData } from '~/lib/homes/profile/nearby';
import { useTranslations } from 'next-intl';
import { ParagraphList } from '~/components/ui/tables/ParagraphList';
import { getSurroundingData } from '~/lib/homes/profile/surrounding';
import { useUser } from '~/hooks/useUser';
import { useAgency } from '~/hooks/useAgency';
import { useMemo } from 'react';
import { isBrokerOrAdmin } from '~/lib/roles';

interface HomeProfileMapProps {
  home: NestedHome;
}

export const HomeProfileMap = ({ home }: HomeProfileMapProps) => {
  const nearby = getNearbyData(home);
  const t = useTranslations('HomeProfile.Accordions');
  const { user } = useUser();
  const { agency } = useAgency();

  // Check if user can edit (broker or admin)
  const canEdit = useMemo(() => {
    if (!user || !agency) return false;
    return isBrokerOrAdmin(user);
  }, [user, agency]);

  // Check if home has coordinates (can be in one of two ways, not sure why, and seems to change lol)
  const hasAddressCoordinates =
    Boolean(home.address?.longitude) && Boolean(home.address?.latitude);
  const hasLocationCoordinates = Boolean(
    (home.address?.location as { coordinates?: unknown[] } | undefined)
      ?.coordinates?.length
  );
  const hasCoordinates = hasAddressCoordinates || hasLocationCoordinates;

  return (
    <Stack gap="10">
      {hasCoordinates && <ProfileMap home={home} />}

      <Stack>
        {/* Surrounding */}
        {getSurroundingData(home).length > 0 && (
          <Stack>
            <Text size="sm" fontWeight="light" color="blackAlpha.800">
              {t('Surrounding.title')}
            </Text>
            <ParagraphList data={getSurroundingData(home)} />
          </Stack>
        )}

        {/* Nearby - Hide when show_address is false */}
        {((nearby && nearby.length > 0) || canEdit) &&
        home.status?.show_address ? (
          <Stack>
            {nearby && nearby.length > 0 && (
              <Text
                size={{ mobile: 'sm', desktop: 'md' }}
                fontWeight="light"
                color="blackAlpha.800"
              >
                {home.nearby_json &&
                home.nearby_json.length > 0 &&
                Math.min(...home.nearby_json.map((item) => item.distance)) <
                  1000
                  ? t('Nearby.title')
                  : t('Nearby.titleFallback')}
              </Text>
            )}
            {home.nearby_version === 2.0 ? (
              <NearbyGoogle home={home} />
            ) : (
              <NearbyWithEditor home={home} />
            )}
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};
